/**
 * plan 枚举的中文显示（接口设计 v0.3 附录 A；接口传英文键值，页面按此表显示中文）。PR3 新写。
 */
import type { BadgeTone } from "@/components/ui/badge";

export const ITEM_STATUS_ZH: Record<string, string> = {
	todo: "待办",
	doing: "进行中",
	wait_customer: "等客户",
	done: "办完",
	cant: "办不了",
	cancelled: "已撤销",
	handoff: "转人工",
};
export const ITEM_STATUS_TONE: Record<string, BadgeTone> = {
	todo: "muted",
	doing: "info",
	wait_customer: "warning",
	done: "success",
	cant: "danger",
	cancelled: "neutral",
	handoff: "warning",
};
export const WAIT_KIND_ZH: Record<string, string> = {
	missing_info: "缺信息",
	choose_object: "选对象",
	confirm: "待确认",
	clarify: "说清撤回对象",
	intent_confirm: "问要不要办",
	handoff_after_confirm: "确认完再转人工",
};
export const STOP_REASON_ZH: Record<string, string> = {
	no_actionable: "没有可推进事项",
	wait_confirm: "等客户确认",
	wait_info: "等客户补信息",
	wait_choice: "等客户选对象",
	wait_clarify: "等客户说清撤回对象",
	step_budget: "本轮步数用完",
	wall_clock: "墙钟到",
	handoff: "转人工",
	empty_plan: "计划为空",
	turn_void: "整轮作废",
};
export const STOP_REASON_TONE: Record<string, BadgeTone> = {
	no_actionable: "muted",
	wait_confirm: "neutral",
	wait_info: "neutral",
	wait_choice: "neutral",
	wait_clarify: "neutral",
	step_budget: "warning",
	wall_clock: "warning",
	handoff: "warning",
	empty_plan: "neutral",
	turn_void: "danger",
};
export const RESUME_ACTION_ZH: Record<string, string> = {
	provide: "补信息",
	confirm: "确认",
	correct: "纠正",
	object_correct: "对象纠正",
	reject: "拒绝",
	unsure: "同意但把握不够",
	new: "新开",
	none: "没有新信息",
};
export const SOURCE_ZH: Record<string, string> = {
	ask: "人工提问",
	eval: "评测",
	bridge: "评测对接桥",
};
export const SOURCE_TONE: Record<string, BadgeTone> = {
	ask: "info",
	eval: "warning",
	bridge: "neutral",
};
export const CHECK_VERDICT_ZH: Record<string, string> = {
	pass: "通过",
	fix: "修正",
	reject: "剔除",
	split: "拆分",
	merge: "合并",
	defer: "到发送时再核",
};
export const ERROR_TYPE_ZH: Record<string, string> = {
	llm_failed: "模型调用失败",
	llm_parse_failed: "模型输出解析失败",
	llm_timeout: "模型超时",
	llm_quota: "模型额度用完",
	tool_error: "工具出错",
	state_invalid: "代码异常",
};

/** 激活时机的一句话：首轮 ／ 第 n 轮激活 ／ 客户提到时激活 */
export function activationText(
	a: { mode?: string; turn?: number | null } | null | undefined,
): string {
	if (!a || a.mode === "first_turn") return "首轮激活";
	if (a.mode === "turn_n") return `第 ${a.turn ?? "?"} 轮激活`;
	if (a.mode === "on_mention") return "客户提到时激活";
	return a.mode ?? "—";
}

export function zh(map: Record<string, string>, key: string | null | undefined): string {
	if (!key) return "—";
	return map[key] ?? key;
}

/* ---- PR5：运行轨迹抽屉用的中文名 ---- */

/** 层＝七环节（结构对齐 S12）；旧记录的 planning ／ execution ／ generation 显示为对应环节 */
export const LAYER_ZH: Record<string, string> = {
	session: "会话",
	signal: "信号",
	recall: "召回",
	board: "事项板",
	decide: "决策",
	execute: "执行把关",
	settle: "结算",
	reply: "回复",
	planning: "事项板",
	execution: "决策",
	generation: "回复",
	eval: "评测",
};

/** 护栏编号 → 一句话名字（《03》§4.1；没登记的照原编号显示） */
export const GUARD_ZH: Record<string, string> = {
	G1: "必填齐全",
	G5: "写前确认",
	G6: "排除换道",
	G8: "订单归属",
	G10: "事项出口约束",
	G11: "候选外工具",
	G12: "换道收尾",
	G13: "动作纠正",
	G14: "行项消歧",
	G16: "同参不重复",
	"新-1": "写对象一致",
	"新-2": "办成要有证据",
	"MULTI-CAND": "多候选裁决",
	幂等: "事项级幂等",
	"EXE-DEDUP": "同对象已提交不再提交",
	"EXE-NOSWAP": "被拦后不换写工具代办",
	"EXE-CARD-SHOWN": "确认句与确认卡一致",
	"EXE-NO-CHANGE": "写入目标与现状相同不提交",
	写结果分类: "写结果分类",
};
export const GUARD_VERDICT_ZH: Record<string, string> = {
	pass: "通过",
	block: "拦下",
	fix: "改道",
	warn: "告警",
	skip: "跳过",
};
export const GUARD_VERDICT_TONE: Record<string, BadgeTone> = {
	pass: "muted",
	skip: "muted",
	block: "danger",
	fix: "warning",
	warn: "warning",
};

/** N2 一步的动作 */
export const N2_ACTION_ZH: Record<string, string> = {
	call_tool: "调用",
	ask: "反问",
	done: "办完",
	cant: "办不了",
};

/** N3 回复类型 */
export const REPLY_TYPE_ZH: Record<string, string> = {
	inform: "说明（有依据）",
	clarify: "澄清",
	confirm: "确认",
	followup: "追问承接",
	chitchat: "闲聊",
	decline: "越界婉拒",
	handoff: "转人工",
};

/** 工具调用来源（照 Loop：真实 ／ 录制回放 ／ 罐头） */
export const TOOL_BACKEND_ZH: Record<string, string> = {
	live: "真实执行",
	recorded: "录制回放",
	fixture: "罐头",
};

/** 写操作分类 */
export const WRITE_CLASS_ZH: Record<string, string> = {
	dry_run: "预演",
	committed: "已执行",
	not_written: "没写成",
	unknown: "结果未知",
	reused: "复用已提交",
};

/** 事项事件（I-08）里在轨迹里显示的几种 */
export const ITEM_EVENT_ZH: Record<string, string> = {
	state_change: "状态",
	question_asked: "问客户",
	evidence: "证据",
	merge: "信息合并",
	marker: "写入标记",
	confirm_void: "确认作废",
	duplicate_confirm: "重复确认",
	object_refined: "对象细化",
	pending_check: "挂起复核",
	unanswered: "未回答",
	no_progress: "没有进展",
	activate_note: "激活说明",
	write_reused: "复用已提交",
	side_question_as_new: "插问另起一件",
};

/** 计划版本对比：事项变化 */
export const PLAN_CHANGE_ZH: Record<string, string> = {
	added: "新增",
	closed: "关闭",
	rewritten: "改写",
	reordered: "改序",
	same: "不变",
	rejected: "剔除",
	fixed: "修正",
	split: "拆分",
};
export const PLAN_CHANGE_TONE: Record<string, BadgeTone> = {
	added: "success",
	closed: "neutral",
	rewritten: "warning",
	reordered: "info",
	same: "muted",
	rejected: "danger",
	fixed: "warning",
	split: "info",
};

/** 计划生成者 */
export const PLAN_GENERATOR_ZH: Record<string, string> = {
	rule: "规则",
	llm: "LLM",
};

/** 候选在事项里的角色 */
export const CANDIDATE_ROLE_ZH: Record<string, string> = {
	primary: "首选",
	alternative: "备选",
	prerequisite: "前置",
	standing: "常备",
};

/** 订单号显示：`gid://shopify/Order/2002` → `2002`（不是 gid 的原样返回） */
export function orderNo(id: unknown): string {
	if (id === null || id === undefined || id === "") return "—";
	const s = String(id);
	const m = /^gid:\/\/shopify\/[A-Za-z]+\/(.+)$/.exec(s);
	return m?.[1] ?? s;
}

/** 判停（I-12 `stop`；没登记的退回事项状态名） */
export const STOP_ZH: Record<string, string> = {
	continue: "继续推进",
	wait_customer: "等客户",
	done: "办完",
	cant: "办不了",
	handoff: "转人工",
	budget: "步数用完",
};

/** 工具返回状态（没登记的照原值显示） */
export const TOOL_STATUS_ZH: Record<string, string> = {
	done: "成功",
	render: "已出结果",
	confirm_gate: "待确认",
	not_found: "查无",
	error: "出错",
	clarify: "需澄清",
	blocked: "被拦",
	empty: "空结果",
};

/** 评测批次状态（接口设计附录 A；PR6） */
export const BATCH_STATUS_ZH: Record<string, string> = {
	queued: "排队",
	running: "运行中",
	stopping: "停止中",
	finished: "完成",
	stopped: "已停",
	interrupted: "中断",
};
export const BATCH_STATUS_TONE: Record<
	string,
	"success" | "warning" | "danger" | "info" | "muted"
> = {
	queued: "muted",
	running: "info",
	stopping: "warning",
	finished: "success",
	stopped: "muted",
	interrupted: "danger",
};

/** 评测三态（PR9；接口设计附录 A） */
export const OUTCOME_ZH: Record<string, string> = {
	pending: "待跑",
	pass: "过",
	fail: "败",
	undetermined: "未判定",
};
export const OUTCOME_TONE: Record<string, BadgeTone> = {
	pending: "muted",
	pass: "success",
	fail: "danger",
	undetermined: "warning",
};

/** 未判定原因（PR7 落地记录 1.4；agent_failed 为 PR7 新增；rate_limited 10-21 A3 单列） */
export const UNDETERMINED_ZH: Record<string, string> = {
	quota: "额度报错",
	timeout: "超时",
	rate_limited: "限流",
	parse_failed: "解析失败",
	sim_failed: "机器顾客出错",
	judge_cannot: "判官判不了",
	agent_failed: "被测 agent 失败",
	stopped_manual: "手动停批",
	stopped_cost: "花费闸停批",
	interrupted: "部署中断",
	not_rejudged: "未重判",
	not_rejudgeable: "不可重判",
};

/** 批次停止原因（A-54 stop_reason） */
export const BATCH_STOP_ZH: Record<string, string> = {
	manual: "手动停",
	cost: "花费闸停",
	quota: "额度报错停",
};

/** 机器顾客性格（与 Loop 页、user_sim.PERSONAS 同名同译） */
export const PERSONA_ZH: Record<string, string> = {
	standard: "标准",
	terse: "惜字如金",
	verbose: "啰嗦",
	impatient: "没耐心",
};
/** 小数统一保留至多 2 位（3.8 照写、0.46699… → 0.47；MVP-0 收尾修复）；空值 → — */
export function fmtNum(n: number | null | undefined, digits = 2): string {
	if (n == null || !Number.isFinite(n)) return "—";
	const f = 10 ** digits;
	return String(Math.round(n * f) / f);
}

/** 稳定分类（《06》§8.2；次数不足 = 有效次数不够规定次数，不进稳定分类） */
export const STABLE_ZH: Record<string, string> = {
	stable_pass: "稳定过",
	flaky: "摇摆",
	stable_fail: "稳定败",
	insufficient: "次数不足",
	unclassified: "不归类",
};
export const STABLE_TONE: Record<string, BadgeTone> = {
	stable_pass: "success",
	flaky: "warning",
	stable_fail: "danger",
	insufficient: "muted",
	unclassified: "muted",
};

/** A/B 题内变化（接口设计附录 A「题内变化」） */
export const CHANGE_ZH: Record<string, string> = {
	flip_good: "翻好",
	flip_bad: "翻坏",
	flaky: "摇摆",
	same: "不变",
	undetermined: "未判定",
	not_rejudged: "未重判",
};
export const CHANGE_TONE: Record<string, BadgeTone> = {
	flip_good: "success",
	flip_bad: "danger",
	flaky: "warning",
	same: "muted",
	undetermined: "muted",
	not_rejudged: "muted",
};
/** 按题表默认排序：翻坏在前（线框 02 待确认 #3） */
export const CHANGE_ORDER = [
	"flip_bad",
	"flip_good",
	"flaky",
	"undetermined",
	"not_rejudged",
	"same",
] as const;

/** A/B 作业状态与分段（PR8 落地记录 §五-10） */
export const AB_STATUS_ZH: Record<string, string> = {
	queued: "排队",
	running: "进行中",
	finished: "完成",
	failed: "失败",
	interrupted: "中断",
};
export const AB_STAGE_ZH: Record<string, string> = {
	run_plan: "跑 plan 批次",
	rejudge: "重判 Loop 批次",
	gate: "口径闸",
	diff: "出差值",
};

/** 机器顾客会话的终止原因（PR7 trial `termination_reason`） */
export const TERMINATION_ZH: Record<string, string> = {
	stop: "客户结束",
	transfer: "转人工",
	transfer_no_handoff: "要转人工·客服没转",
	out_of_scope: "超出范围",
	max_turns: "到轮数上限",
};
